import {
  useEffect, useMemo,
} from 'react';
import {
  Control, Controller, UseFormSetValue, UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useCurrency from 'hooks/useCurreny';
import useGetMetric from 'hooks/useGetMetric';
import { Mixpanel } from 'mixpanel';
import useUser from 'api/hooks/useUser';
import Button from 'components/Button';
import EconomicFeasibilityResult from 'components/EconomicFeasibilityResult';
import MaskInput from 'components/MaskedInput';
import Select from 'components/Select';
import { numberFormatter, unMaskInput } from 'utils/helpers';
import { AlkaliProps, SelectProps } from 'utils/types';
import { IFeasibilityCheck } from 'features/FeasibilityCheck';
import { cn } from 'utils/cn';

interface Props {
  data: AlkaliProps[];
  omyaData: AlkaliProps[];
  setActiveTab: (value: number) => void;
  setValue: UseFormSetValue<IFeasibilityCheck>;
  formWatch: UseFormWatch<IFeasibilityCheck>;
  control: Control<IFeasibilityCheck>;
  isPDF?: boolean;
}

const EconomicFeasibility = ({
  data,
  omyaData,
  setActiveTab,
  control,
  setValue,
  formWatch,
  isPDF,
}: Props) => {
  const { t } = useTranslation();
  const { data: user } = useUser();

  const {
    consumption,
    price,
    costDelivered,
    alkali,
    concentration,
    omya,
    concentrationOmya,
    annualExpenditure,
    expectedProductConsumption,
  } = formWatch('economic');
  const applicationData = formWatch('application');

  const { ton } = useGetMetric();
  const { currencyBlue } = useCurrency();

  useEffect(() => {
    const watchPrice1 = unMaskInput(price);
    const watchConsumption1 = unMaskInput(consumption);

    if (price?.length > 0 && consumption?.length) {
      setValue('economic.annualExpenditure', numberFormatter(Number(unMaskInput(watchPrice1)) * Number(unMaskInput(watchConsumption1))));
    } else {
      setValue('economic.annualExpenditure', 0);
    }
  }, [price, consumption]);

  const handleNext = () => {
    setActiveTab(3);

    Mixpanel.track('Feasibility Check Step 2', {
      role: user?.role?.name,
      organization: user?.organization,
    });
  };

  const alkaliSelectOptions = useMemo(() => data?.map((alkali) => ({
    value: alkali?.['Material A'],
    label: alkali?.['Material A'],
  })), [data]);

  const omyaSelectOptions = useMemo(() => omyaData
    ?.filter((value) => {
      if (applicationData?.product?.value.includes(t('other'))) {
        return value['Material A'].includes('Omya');
      } else {
        return applicationData?.productsOptions?.includes(value['Material A'].replace(/\s\s+/g, ' '));
      }
    })
    .map((omya) => ({
      value: omya?.['Material A'].replace(/\s\s+/g, ' '),
      label: omya?.['Material A'].replace(/\s\s+/g, ' '),
    })), [omyaData, applicationData?.productsOptions, applicationData?.product]);

  useEffect(() => {
    const currentAlkali = data?.find(
      (product) => product['Material A'] === alkali?.value &&
        product?.Concentration === Number(concentration?.value),
    );

    const currentOmya = omyaData?.find((product) => {
      if (omya) {
        return (
          product['Material A'].replace(/\s\s+/g, ' ').includes(omya.value) &&
          product?.Concentration === Number(concentrationOmya?.value)
        );
      }
    });

    if (
      !currentAlkali ||
      !currentOmya ||
      !consumption ||
      !concentration?.value ||
      !concentrationOmya?.value
    ) {
      setValue('economic.expectedProductConsumption', 0);

      return;
    }

    const alkaliConsumption = Number(unMaskInput(consumption));
    const alkaliEq =
      currentAlkali?.Stoichiometry / currentAlkali?.['Mol. Weight'];

    const alkaliConcentration = Number(concentration?.value);
    const alkaliValue = alkaliConsumption * alkaliEq * alkaliConcentration;

    const omyaEq = currentOmya?.Stoichiometry / currentOmya?.['Mol. Weight'];

    const omyaConcentration = Number(concentrationOmya?.value);

    const omyaValue = omyaEq * omyaConcentration;

    setValue('economic.expectedProductConsumption', Math.round(alkaliValue / omyaValue));
  }, [alkali?.value, concentration?.value, concentrationOmya?.value, data, omya, omyaData, consumption]);

  function getUniqueListBy(arr: SelectProps[], key: string) {
    return [...new Map(arr?.map((item: any) => [item[key], item])).values()];
  }

  const arr1 = getUniqueListBy(alkaliSelectOptions, 'value');
  const arr2 = getUniqueListBy(omyaSelectOptions, 'value');

  const getConcentrationList = useMemo(() => {
    const result: AlkaliProps[] = [];

    if (alkali?.value) {
      data?.forEach((alkaliItem: AlkaliProps) => {
        if (alkaliItem?.['Material A'] === alkali?.value) {
          result.push(alkaliItem);
        }
      });
    }

    return result.map((concentration: AlkaliProps) => ({
      value: String(concentration.Concentration),
      label:
          String(concentration.Concentration) === '100'
            ? String(concentration.Concentration) + ' = Powder'
            : String(concentration.Concentration) + ' %',
    }));
  }, [alkali?.value, data]);

  const getConcentrationListOmya = useMemo(() => {
    const result: AlkaliProps[] = [];

    if (omya?.value) {
      omyaData?.forEach((omyaItem: AlkaliProps) => {
        if (omyaItem?.['Material A'].replace(/\s\s+/g, ' ') === omya?.value.replace(/\s\s+/g, ' ')) {
          result.push(omyaItem);
        }
      });
    }

    return result.map((concentration: AlkaliProps) => ({
      value: String(concentration.Concentration),
      label:
          String(concentration.Concentration) === '100'
            ? String(concentration.Concentration) + ' = Powder'
            : String(concentration.Concentration) + ' %',
    }));
  }, [omya?.value, applicationData?.product?.value]);

  useEffect(() => {
    if (getConcentrationList[0]) {
      setValue('economic.concentration', getConcentrationList[0]);
    }
  }, [alkali?.value, getConcentrationList]);

  useEffect(() => {
    if (getConcentrationListOmya[0]) {
      setValue('economic.concentrationOmya', getConcentrationListOmya[0]);
    }
  }, [omya, applicationData?.product?.value]);

  const disabledState = useMemo(() => !(alkali?.value &&
        omya?.value &&
        concentration?.value &&
        concentrationOmya?.value &&
        price?.length > 0 &&
        costDelivered?.length > 0 &&
        consumption?.length), [
    alkali?.value,
    omya?.value,
    concentration?.value,
    concentrationOmya?.value,
    price,
    costDelivered,
    consumption,
  ]);

  useEffect(() => {
    if (applicationData?.product?.value === t('other')) {
      setValue('economic.omya', null);
    } else {
      setValue('economic.omya', {
        label: applicationData?.product?.value || '',
        value: applicationData?.product?.value || '',
      });
    }
  }, [applicationData?.product?.value]);

  return (
    <form>
      <div className={cn('border border-primitiveGrey p-8 rounded-2xl', {
        'md:py-6 md:px-4 rounded-2xl':!isPDF,
      })}>
        <div className="text-black text-xl font-bold mb-8">
          {t('economicFeasibility')}
        </div>
        <div className={cn(
          'flex justify-between',
          { 'md:flex-col': !isPDF },
        )}>
          <div className={cn(
            'w-1/2 border-r border-primitiveGrey  pr-8',
            { 'md:border-b md:border-r-0 md:w-full md:pr-0': !isPDF },
          )}>
            <div className="text-black font-bold text-lg md:text-lg">
              {t('currentProductTitle')}
            </div>
            <div className="my-6 break-inside-avoid">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value ? [value] : []}
                    options={arr1}
                    placeholder={t('specify')}
                    title={t('currentAlkali')}
                    onChange={onChange}
                  />
                )}
                name="economic.alkali"
              />
            </div>
            <div className="my-6 break-inside-avoid">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value ? [value] : []}
                    options={getConcentrationList}
                    placeholder={t('specify')}
                    title={`${t('concentration')} (wt.%)`}
                    onChange={onChange}
                  />
                )}
                name="economic.concentration"
              />
            </div>
            <div className="my-6 w-full break-inside-avoid">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <MaskInput
                    titleClassName="top-[-9px] left-[8px]"
                    className="w-full"
                    title={`${t('annualConsumption')} (${ton})`}
                    placeholder={t('enterAnnualConsumption')}
                    onChange={onChange}
                    value={value}
                  />
                )}
                name="economic.consumption"
              />
            </div>
            <div className="my-6 w-full break-inside-avoid">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <MaskInput
                    titleClassName="top-[-9px] left-[8px]"
                    className="w-full"
                    title={`${t('price')} (${currencyBlue}/${ton})`}
                    placeholder={t('enterPrice')}
                    onChange={onChange}
                    value={value}
                  />
                )}
                name="economic.price"
              />
            </div>
            <div className="my-6 w-full relative break-inside-avoid">
              <div className="absolute top-[-10px] left-2 text-sm text-black">
                {t('currentExpenditure')}
              </div>
              <div className="bg-[#e2e2e2] p-3 rounded-xl text-secondaryText cursor-not-allowed">
                {annualExpenditure ? annualExpenditure : '0'}{' '}
                {currencyBlue}/{t('year')}
              </div>
            </div>
          </div>
          <div className={cn(
            'w-1/2 pl-8',
            { 'md:w-full md:pl-0': !isPDF },
          )}>
            <div className="text-black font-bold text-lg md:text-lg md:mt-6">
              {t('omyaOffered')}
            </div>
            <div className="my-6 break-inside-avoid">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value ? [value] : []}
                    options={arr2}
                    placeholder={t('specify')}
                    title={t('product')}
                    onChange={onChange}
                  />
                )}
                name="economic.omya"
              />
            </div>
            <div className="my-6 break-inside-avoid">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value ? [value] : []}
                    options={getConcentrationListOmya}
                    placeholder={t('selectValue')}
                    title={`${t('concentration')} (wt.%)`}
                    onChange={onChange}
                  />
                )}
                name="economic.concentrationOmya"
              />
            </div>
            <div className="my-6 w-full break-inside-avoid">
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <MaskInput
                    titleClassName="top-[-9px] left-[8px]"
                    className="w-full"
                    title={`${t('costDelivered')} (${currencyBlue}/${ton})`}
                    placeholder={t('enterAnnualConsumption')}
                    onChange={onChange}
                    value={value}
                  />
                )}
                name="economic.costDelivered"
              />
            </div>
            {costDelivered && (
              <EconomicFeasibilityResult
                costDelivered={unMaskInput(costDelivered)}
                annualExpenditure={annualExpenditure}
                expectedConsumption={expectedProductConsumption}
              />
            )}
          </div>
        </div>
        {!isPDF && (
          <div className="mt-8 flex justify-center md:mt-6">
            <Button
              onClick={() => setActiveTab(1)}
              className="mr-4"
              btnType="secondary"
            >
              {t('back')}
            </Button>
            <Button disabled={disabledState} onClick={handleNext}>
              {t('next')}
            </Button>
          </div>
        )}
      </div>
    </form>
  );
};

export default EconomicFeasibility;
